import { useState, useEffect, useRef } from "react";
import RightTimeApi from "@/API"; // Ensure you import RightTimeApi if it's used
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { MdCheckBoxOutlineBlank, MdCheckBox, MdCameraswitch } from "react-icons/md";
import { toast } from "react-toastify";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { CiEdit } from "react-icons/ci";
import { RxDragHandleDots1 } from "react-icons/rx";
import ToolTip from "../ToolTip";
import { isDepartmentRequestField } from "@/utils/formSelectionOptions";


export default function RequestFormFieldsTable(props: any) {
  const tableMode = props.mode || "company";
  const isCompanyMode = tableMode === "company";
  const isTestTypeMode = tableMode === "testType" || tableMode === "testTypeAdmin" || tableMode === "testTypeDraft";
  const isTestTypeAdminMode = tableMode === "testTypeAdmin" || (tableMode === "testType" && !!props.testTypeId);
  const isTestTypeDraftMode = tableMode === "testTypeDraft";
  const [editField, updateEditField] = useState<any>({});
  const [fieldTypes, updateFieldTypes] = useState<any[]>([]);
  const [availableTTFieldTypes, updateAvailableTTFieldTypes] = useState<any[]>([]);
  const [draftFields, updateDraftFields] = useState<any[]>([]);
  const [draggedFieldId, setDraggedFieldId] = useState<number | null>(null);
  const [dragOverFieldId, setDragOverFieldId] = useState<number | null>(null);
  const [draggedFieldContext, setDraggedFieldContext] = useState<"company" | "testTypeAdmin" | "testTypeDraft" | null>(null);
  const [isDragOverEndZone, setIsDragOverEndZone] = useState(false);
  const isHydratingDraftRef = useRef(false);
  const onTestTypeFieldsChangeCallbackRef = useRef<any>(null);
  onTestTypeFieldsChangeCallbackRef.current = props.onTestTypeFieldsChange;
  const isEditingAnyField = Object.keys(editField).length > 0;
  const departmentsEnabled = props.departmentsEnabled ?? props.tabData?.company?.departmentsEnabled;

  function isDepartmentSelectCatalogEntry(ft: any) {
    return isDepartmentRequestField({
      fieldId: ft?.id ?? ft?.key,
      fieldElementName: ft?.name ?? ft?.value,
    });
  }

  function catalogFieldTypesAllowingDepartmentSelect(catalog: any[] | null | undefined) {
    const list = Array.isArray(catalog) ? catalog : [];
    if (departmentsEnabled !== false) return list;
    return list.filter((ft) => !isDepartmentSelectCatalogEntry(ft));
  }

  useEffect(() => {
    if (!isTestTypeDraftMode) return;
    const normalized = (props.testTypeFields ?? []).map((f: any) => ({
      id: f.id,
      testRequestFieldId: f.testRequestFieldId ?? f.id,
      testRequestFieldName: f.testRequestFieldName,
      label: f.label ?? "",
      required: !!f.required,
      proctorProvided: !!f.proctorProvided,
    }));
    isHydratingDraftRef.current = true;
    updateDraftFields(normalized);
  }, [isTestTypeDraftMode, props.testTypeFields]);

  useEffect(() => {
    if (!isTestTypeDraftMode) return;
    if (isHydratingDraftRef.current) {
      isHydratingDraftRef.current = false;
      return;
    }
    if ((draftFields ?? []).length === 0 && (props.testTypeFields ?? []).length > 0)
      return;
    onTestTypeFieldsChangeCallbackRef.current?.(draftFields);
  }, [isTestTypeDraftMode, draftFields, props.testTypeFields]);

  useEffect(() => {
    recalcFieldTypes();
  }, [props.fieldTypes, props.activeTestType?.fieldTypes, props.data?.fieldTypes, departmentsEnabled]);

  useEffect(() => {
    recalcFieldTypes();
  }, [editField]);

  useEffect(() => {
    recalcTTFieldTypes();
  }, [props.fieldTypes, props.testTypeFields, editField?.id, departmentsEnabled]);

  useEffect(() => {
    recalcDraftFieldTypes();
  }, [props.fieldTypes, draftFields, editField?.id, departmentsEnabled]);

  function recalcTTFieldTypes() {
    if (!isTestTypeAdminMode || !props.fieldTypes) return;
    const usedIds = new Set((props.testTypeFields ?? []).map((f: any) => f.testRequestFieldId));
    if (editField && editField.id > 0) usedIds.delete(editField.testRequestFieldId);
    const available = catalogFieldTypesAllowingDepartmentSelect(props.fieldTypes as any[]).filter((ft: any) => !usedIds.has(ft.id));
    available.unshift({ id: 0, name: "--- Pick One ---" });
    updateAvailableTTFieldTypes(available);
  }

  function recalcDraftFieldTypes() {
    if (!isTestTypeDraftMode || !props.fieldTypes) return;
    const usedIds = new Set((draftFields ?? []).map((f: any) => f.testRequestFieldId));
    if (editField && editField.id > 0) usedIds.delete(editField.testRequestFieldId);
    const available = catalogFieldTypesAllowingDepartmentSelect(props.fieldTypes as any[]).filter((ft: any) => !usedIds.has(ft.id));
    if (editField && editField.id > 0 && editField.testRequestFieldId > 0) {
      const existing = (props.fieldTypes as any[]).find((ft: any) => ft.id === editField.testRequestFieldId);
      if (existing && !available.find((ft: any) => ft.id === existing.id)) {
        available.push(existing);
      }
    }
    available.unshift({ id: 0, name: "--- Pick One ---" });
    updateAvailableTTFieldTypes(available);
  }

  function blankDraftField() {
    return { id: 0, testRequestFieldId: 0, label: "", required: false, proctorProvided: false };
  }

  function addDraftField() {
    recalcDraftFieldTypes();
    updateEditField(blankDraftField());
  }

  function startEditDraftField(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    const field = (draftFields ?? []).find((f: any) => f.id === id);
    if (field) {
      updateEditField({ ...field });
      recalcDraftFieldTypes();
    }
  }

  function saveDraftField() {
    if (!editField.testRequestFieldId) {
      toast.error("Please select a field type");
      return;
    }

    const selected = (props.fieldTypes ?? []).find((f: any) => f.id === editField.testRequestFieldId);
    const nextLabel = (editField.label || "").trim() || selected?.name || "";
    const nextName = selected?.name || editField.testRequestFieldName || "";

    if (editField.id > 0) {
      updateDraftFields((prev) => prev.map((f: any) =>
        f.id === editField.id
          ? {
            ...f,
            testRequestFieldId: editField.testRequestFieldId,
            testRequestFieldName: nextName,
            label: nextLabel,
            required: !!editField.required,
            proctorProvided: !!editField.proctorProvided,
          }
          : f
      ));
    }
    else {
      const nextId = editField.testRequestFieldId;
      updateDraftFields((prev) => [
        ...prev,
        {
          id: nextId,
          testRequestFieldId: editField.testRequestFieldId,
          testRequestFieldName: nextName,
          label: nextLabel,
          required: !!editField.required,
          proctorProvided: !!editField.proctorProvided,
        },
      ]);
    }

    updateEditField({});
  }

  function deleteDraftField(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateDraftFields((prev) => prev.filter((f: any) => f.id !== id));
    if (editField?.id === id) updateEditField({});
  }

  function toggleDraftRequired(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateDraftFields((prev) => prev.map((f: any) => f.id === id ? { ...f, required: !f.required } : f));
  }

  function toggleDraftProctorProvided(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateDraftFields((prev) => prev.map((f: any) => f.id === id ? { ...f, proctorProvided: !f.proctorProvided } : f));
  }

  function toggleAllRequiredDraft() {
    if (!Array.isArray(draftFields) || draftFields.length === 0) return;
    const requiredCount = draftFields.filter((field: any) => field.required).length;
    const shouldEnableAll = requiredCount <= draftFields.length / 2;
    updateDraftFields((prev) => prev.map((field: any) => ({ ...field, required: shouldEnableAll })));
  }

  function toggleAllProctorProvidedDraft() {
    if (!Array.isArray(draftFields) || draftFields.length === 0) return;
    const providedCount = draftFields.filter((field: any) => field.proctorProvided).length;
    const shouldEnableAll = providedCount <= draftFields.length / 2;
    updateDraftFields((prev) => prev.map((field: any) => ({ ...field, proctorProvided: shouldEnableAll })));
  }

  function blankTTField() {
    return { id: 0, testTypeId: props.testTypeId, testRequestFieldId: 0, label: "", required: false, proctorProvided: false };
  }

  function addTTField() {
    recalcTTFieldTypes();
    updateEditField(blankTTField());
  }

  function startEditTTField(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    const field = (props.testTypeFields ?? []).find((f: any) => f.id === id);
    if (field) {
      updateEditField({ ...field });
      recalcTTFieldTypes();
    }
  }

  async function saveTTFieldAsync() {
    if (!editField.testRequestFieldId) {
      toast.error("Please select a field type");
      return;
    }
    props.tabData?.updateSpinner(true);
    const resp = await RightTimeApi.sendAsync("/TestTypeField", editField);
    props.tabData?.updateSpinner(false);
    if (resp && resp.ok) {
      toast.success(editField.id > 0 ? "Field updated" : "Field added");
      updateEditField({});
      props.updateData && props.updateData();
    }
  }

  async function deleteTTFieldAsync(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    props.tabData?.updateSpinner(true);
    const resp = await RightTimeApi.deleteAsync("/TestTypeField/" + id);
    props.tabData?.updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Field removed");
      props.updateData && props.updateData();
    }
  }

  async function moveTTFieldAsync(fieldId, moveUp) {
    const resp = await RightTimeApi.putAsync("/TestTypeFieldMove", { fieldId, testTypeId: props.testTypeId, moveUp });
    return !!(resp && resp.ok);
  }

  async function toggleTTRequiredAsync(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    props.tabData?.updateSpinner(true);
    const resp = await RightTimeApi.putAsync(`/TestTypeField/${id}/ToggleRequired`, {});
    props.tabData?.updateSpinner(false);
    if (resp && resp.ok) props.updateData && props.updateData();
  }

  async function toggleTTPPAsync(e) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    props.tabData?.updateSpinner(true);
    const resp = await RightTimeApi.putAsync(`/TestTypeField/${id}/ToggleProctorProvided`, {});
    props.tabData?.updateSpinner(false);
    if (resp && resp.ok) props.updateData && props.updateData();
  }

  function handleFieldDragStart(e, fieldId, context) {
    if (isEditingAnyField) return;
    setDraggedFieldId(fieldId);
    setDraggedFieldContext(context);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(fieldId));
  }

  function handleFieldDragOver(e, fieldId, context) {
    if (!draggedFieldId || draggedFieldContext !== context) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsDragOverEndZone(false);
    setDragOverFieldId(fieldId);
  }

  function handleFieldDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverFieldId(null);
      setIsDragOverEndZone(false);
    }
  }

  function handleFieldDragEnd() {
    setDraggedFieldId(null);
    setDragOverFieldId(null);
    setDraggedFieldContext(null);
    setIsDragOverEndZone(false);
  }

  function getOrderedListByContext(context) {
    if (context === "testTypeDraft") return draftFields ?? [];
    if (context === "testTypeAdmin") return props.testTypeFields ?? [];
    return props.activeTestType?.fields ?? [];
  }

  function reorderArrayById(items, draggedId, targetId) {
    const draggedIndex = items.findIndex((item) => item.id === draggedId);
    const targetIndex = items.findIndex((item) => item.id === targetId);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return null;

    const reordered = [...items];
    const [moved] = reordered.splice(draggedIndex, 1);
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    reordered.splice(insertIndex, 0, moved);

    return { reordered, fromIndex: draggedIndex, toIndex: insertIndex };
  }

  function moveItemToEndById(items, draggedId) {
    const draggedIndex = items.findIndex((item) => item.id === draggedId);
    if (draggedIndex < 0 || draggedIndex === items.length - 1) return null;

    const reordered = [...items];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.push(moved);

    return { reordered, fromIndex: draggedIndex, toIndex: reordered.length - 1 };
  }

  async function persistApiReorderBySteps(context, fieldId, fromIndex, toIndex) {
    if (fromIndex === toIndex) return true;

    const moveUp = toIndex < fromIndex;
    const moveCount = Math.abs(toIndex - fromIndex);

    props.tabData?.updateSpinner(true);
    let ok = true;
    for (let i = 0; i < moveCount; i++) {
      if (context === "testTypeAdmin") {
        const moved = await moveTTFieldAsync(fieldId, moveUp);
        if (!moved) {
          ok = false;
          break;
        }
      }
      else {
        const moved = await moveFieldAsync(fieldId, moveUp);
        if (!moved) {
          ok = false;
          break;
        }
      }
    }
    props.tabData?.updateSpinner(false);

    return ok;
  }

  async function handleFieldDropAtTarget(e, targetFieldId, context) {
    e.preventDefault();
    setDragOverFieldId(null);
    setIsDragOverEndZone(false);

    if (!draggedFieldId || draggedFieldContext !== context || draggedFieldId === targetFieldId) {
      handleFieldDragEnd();
      return;
    }

    const ordered = getOrderedListByContext(context);
    const reorderResult = reorderArrayById(ordered, draggedFieldId, targetFieldId);
    if (!reorderResult) {
      handleFieldDragEnd();
      return;
    }

    if (context === "testTypeDraft") {
      updateDraftFields(reorderResult.reordered);
      handleFieldDragEnd();
      return;
    }

    const ok = await persistApiReorderBySteps(context, draggedFieldId, reorderResult.fromIndex, reorderResult.toIndex);
    if (ok) {
      props.updateData && props.updateData();
    }
    else {
      toast.error("Field order could not be saved.");
    }

    handleFieldDragEnd();
  }

  async function handleFieldDropAtEnd(e, context) {
    e.preventDefault();

    if (!draggedFieldId || draggedFieldContext !== context) {
      handleFieldDragEnd();
      return;
    }

    const ordered = getOrderedListByContext(context);
    const reorderResult = moveItemToEndById(ordered, draggedFieldId);
    if (!reorderResult) {
      handleFieldDragEnd();
      return;
    }

    if (context === "testTypeDraft") {
      updateDraftFields(reorderResult.reordered);
      handleFieldDragEnd();
      return;
    }

    const ok = await persistApiReorderBySteps(context, draggedFieldId, reorderResult.fromIndex, reorderResult.toIndex);
    if (ok) {
      props.updateData && props.updateData();
    }
    else {
      toast.error("Field order could not be saved.");
    }

    handleFieldDragEnd();
  }

  function renderDropZoneRow(context) {
    if (!draggedFieldId || draggedFieldContext !== context) return null;

    return (
      <tr
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDragOverFieldId(null);
          setIsDragOverEndZone(true);
        }}
        onDragLeave={handleFieldDragLeave}
        onDrop={(e) => handleFieldDropAtEnd(e, context)}
        className={`fieldReorderDropZone${isDragOverEndZone ? " isDragOver" : ""}`}
      >
        <td colSpan={6} className="fieldReorderDropZoneCell">
          Drop here to move to end
        </td>
      </tr>
    );
  }

  function renderTTAdminStaticRow(field, index) {
    const isDragging = draggedFieldId === field.id && draggedFieldContext === "testTypeAdmin";
    const isDraggedOver = dragOverFieldId === field.id && draggedFieldContext === "testTypeAdmin" && !isDragOverEndZone;
    return (
      <tr
        key={"ttaf" + field.id}
        onDragOver={(e) => handleFieldDragOver(e, field.id, "testTypeAdmin")}
        onDragLeave={handleFieldDragLeave}
        onDrop={(e) => handleFieldDropAtTarget(e, field.id, "testTypeAdmin")}
        className={`draggableFieldRow${isDragging ? " isDragging" : ""}${isDraggedOver ? " isDragOver" : ""}`}
      >
        <td
          className={`dragHandleCell${isDragging ? " isGrabbing" : ""}`}
          onClick={(e) => e.stopPropagation()}
          draggable={!isEditingAnyField}
          onDragStart={(e) => handleFieldDragStart(e, field.id, "testTypeAdmin")}
          onDragEnd={handleFieldDragEnd}
          title="Drag to reorder"
        >
          <RxDragHandleDots1 className="actionIcon dragHandle" />
        </td>
        <td>{field.testRequestFieldName}</td>
        <td>{field.label}</td>
        <td className="center" onClick={toggleTTRequiredAsync} data-id={field.id}>
          {field.required ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td className="center" onClick={toggleTTPPAsync} data-id={field.id}>
          {field.proctorProvided ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td>
          <CiEdit className="actionIcon editIcon" data-id={field.id} onClick={startEditTTField} title="Edit" />
          <IoTrashOutline className="actionIcon deleteIcon" data-id={field.id} onClick={deleteTTFieldAsync} />
        </td>
      </tr>
    );
  }

  function renderTTAdminEditRow(field) {
    return (
      <tr key={"ttae" + (field.id ?? "new")} className="editRow">
        <td></td>
        <td>
          <select className="testTypeSelect" value={editField.testRequestFieldId ?? 0} title="Field type" aria-label="Field type"
            onChange={(e) => {
              const id = parseInt(e.target.value);
              const ft = (props.fieldTypes ?? []).find((f: any) => f.id === id);
              updateEditField({ ...editField, testRequestFieldId: id, label: ft ? ft.name : editField.label });
            }}>
            {availableTTFieldTypes.map((ft: any) => (
              <option key={"ttft" + ft.id} value={ft.id}>{ft.name}</option>
            ))}
          </select>
        </td>
        <td>
          <input type="text" value={editField.label ?? ""} className="inlineEdit" style={{ width: "180px" }}
            placeholder="Label" onChange={(e) => updateEditField({ ...editField, label: e.target.value })} />
        </td>
        <td className="center" onClick={() => updateEditField({ ...editField, required: !editField.required })}>
          {editField.required ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td className="center" onClick={() => updateEditField({ ...editField, proctorProvided: !editField.proctorProvided })}>
          {editField.proctorProvided ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td>
          <RiSave3Fill className="save actionIcon" onClick={saveTTFieldAsync} title="Save" />
          <ImCancelCircle className="cancel actionIcon" onClick={() => updateEditField({})} title="Cancel" />
        </td>
      </tr>
    );
  }

  function renderTTField(field, index) {
    return editField && editField.id === field.id
      ? renderTTAdminEditRow(field)
      : renderTTAdminStaticRow(field, index);
  }

  function recalcFieldTypes() {
    if (!isCompanyMode) return;
    if (props && props.activeTestType && props.activeTestType.fields && props.data.fieldTypes) {
      const activeFieldTypeIds = new Set(
        props.activeTestType.fields.map((ft) => ft.field)
      );
      const filteredFieldTypes = catalogFieldTypesAllowingDepartmentSelect(props.data.fieldTypes).filter(
        (ft) =>
          !activeFieldTypeIds.has(ft.value) ||
          String(ft.value).localeCompare("Custom", undefined, { sensitivity: "accent" }) === 0
      );
      if (editField && editField.field && filteredFieldTypes.findIndex(ft => ft.value === editField.field) < 0) {
        filteredFieldTypes.push({ key: editField.field, value: editField.field });
      }
      filteredFieldTypes.push({ key: "", value: "--- Pick One ---" });

      // Sort the filteredFieldTypes by value
      filteredFieldTypes.sort((a, b) => a.value.localeCompare(b.value));

      updateFieldTypes(filteredFieldTypes);
    }
  }

  async function moveFieldAsync(fieldId, moveup) {
    const data = {
      fieldId,
      moveup,
      companyId: props.tabData.company.id,
      companyTestTypeId: props.activeTestType.companyTestType.id,
    };
    const resp = await RightTimeApi.putAsync("/CompanyTestTypeFieldMove", data);
    return !!(resp && resp.ok);
  }

  async function deleteFieldAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    props.tabData.updateSpinner(true);
    const resp = await RightTimeApi.deleteAsync("/CompanyTestTypeField/" + id);
    props.tabData.updateSpinner(false);
    if (resp && resp.ok) {
      props.updateData();
      toast.success("Field removed");
    }
  }

  function blankField() {
    return {
      companyTestTypeId: props.activeTestType.companyTestType.id,
      label: "",
      field: "",
      required: false,
      id: 0,
    };
  }

  function addField() {
    recalcFieldTypes();
    updateEditField(blankField());
  }

  function onCancelEditField() {
    updateEditField({});
  }

  async function onSaveFieldAsync() {
    props.tabData.updateSpinner(true);
    const resp = await RightTimeApi.sendAsync("/CompanyTestTypeField", editField);
    props.tabData.updateSpinner(false);
    if (resp && resp.ok) {
      if (editField.id > 0)
        toast.success("Field Updated");
      else
        toast.success("Field Added");
      updateEditField({});
      props.updateData();
    }
  }

  async function toggleFormFieldRequiredAsync(e) {
    let fieldId = e.currentTarget.getAttribute("data-id");
    if (fieldId) {
      const callData = {
        companyTestTypeId: props.activeTestType.companyTestType.id,
        fieldId: parseInt(fieldId),
      };
      props.tabData.updateSpinner(true);
      const resp = await RightTimeApi.putAsync("/ToggleCompanyTestTypeFieldRequired", callData);
      props.tabData.updateSpinner(false);
      if (resp && resp.ok) {
        if (resp.jsonData.required)
          toast.success(
            "Field " + resp.jsonData.fieldLabel + " is now required"
          );
        else
          toast.success(
            "Field " + resp.jsonData.fieldLabel + " no longer required"
          );
        props.updateData();
      }
    }
  }

  async function toggleAllRequiredAsync() {
    if (!props?.activeTestType?.companyTestType?.id) return;
    if (!Array.isArray(props.activeTestType.fields) || props.activeTestType.fields.length === 0) return;

    const totalFields = props.activeTestType.fields.length;
    const requiredCount = props.activeTestType.fields.filter((field) => field.required).length;
    const shouldEnableAll = requiredCount <= totalFields / 2;

    const fieldsToToggle = props.activeTestType.fields.filter((field) =>
      shouldEnableAll ? !field.required : field.required
    );

    if (fieldsToToggle.length === 0) return;

    props?.tabData?.updateSpinner(true);
    await Promise.all(
      fieldsToToggle.map((field) =>
        RightTimeApi.putAsync("/ToggleCompanyTestTypeFieldRequired", {
          companyTestTypeId: props.activeTestType.companyTestType.id,
          fieldId: field.id,
        })
      )
    );
    props?.tabData?.updateSpinner(false);
    props.updateData();
  }

  async function updateFormDataAsync(e) {
    const fieldName = e.currentTarget.getAttribute("data-field");
    let value = e.currentTarget.value;
    if (fieldName) {
      let dataType = e.currentTarget.getAttribute("data-type");
      if (dataType === "boolean") value = !editField[fieldName] || false;
      if (dataType === "int") value = parseInt(value);
      if (fieldName === "fieldId") {
        const field = fieldTypes.find((f) => f.key === value)
        updateEditField({ ...editField, [fieldName]: value, label: field.value });
      }
      else
        updateEditField({ ...editField, [fieldName]: value });
    }
  }
  async function onEditAsync(e) {
    let f = props.activeTestType.fields.find(
      (f) => f.id === parseInt(e.currentTarget.getAttribute("data-id"))
    );
    updateEditField(f);
  }

  function renderEditActionField(f) {
    return (
      <>
        <RiSave3Fill
          className="save actionIcon"
          onClick={onSaveFieldAsync}
          title="Add"
        />
        <ImCancelCircle
          className="cancel actionIcon"
          onClick={onCancelEditField}
          title="Cancel"
        />
      </>
    );
  }
  function renderStaticActionField(f) {
    return (
      <>
        <CiEdit
          className="actionIcon editIcon"
          data-id={f.id}
          title="Edit Test Status"
          onClick={onEditAsync}
        />
        <IoTrashOutline
          className="actionIcon deleteIcon"
          data-id={f.id}
          onClick={deleteFieldAsync}
        />
      </>
    );
  }

  function renderActionField(f, edit) {
    return edit ? renderEditActionField(f) : renderStaticActionField(f);
  }

  function renderDraftStaticRow(field, index) {
    const isDragging = draggedFieldId === field.id && draggedFieldContext === "testTypeDraft";
    const isDraggedOver = dragOverFieldId === field.id && draggedFieldContext === "testTypeDraft" && !isDragOverEndZone;
    return (
      <tr
        key={"ttdf" + field.id}
        onDragOver={(e) => handleFieldDragOver(e, field.id, "testTypeDraft")}
        onDragLeave={handleFieldDragLeave}
        onDrop={(e) => handleFieldDropAtTarget(e, field.id, "testTypeDraft")}
        className={`draggableFieldRow${isDragging ? " isDragging" : ""}${isDraggedOver ? " isDragOver" : ""}`}
      >
        <td
          className={`dragHandleCell${isDragging ? " isGrabbing" : ""}`}
          onClick={(e) => e.stopPropagation()}
          draggable={!isEditingAnyField}
          onDragStart={(e) => handleFieldDragStart(e, field.id, "testTypeDraft")}
          onDragEnd={handleFieldDragEnd}
          title="Drag to reorder"
        >
          <RxDragHandleDots1 className="actionIcon dragHandle" />
        </td>
        <td>{field.testRequestFieldName}</td>
        <td>{field.label}</td>
        <td className="center" onClick={toggleDraftRequired} data-id={field.id}>
          {field.required ? <MdCheckBox className="checkbox checked actionIcon" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />}
        </td>
        <td className="center" onClick={toggleDraftProctorProvided} data-id={field.id}>
          {field.proctorProvided ? <MdCheckBox className="checkbox checked actionIcon" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />}
        </td>
        <td>
          {!isEditingAnyField && (
            <>
              <CiEdit className="actionIcon editIcon" data-id={field.id} onClick={startEditDraftField} title="Edit" />
              <IoTrashOutline className="actionIcon deleteIcon" data-id={field.id} onClick={deleteDraftField} />
            </>
          )}
        </td>
      </tr>
    );
  }

  function renderDraftEditRow(field) {
    return (
      <tr key={"ttde" + (field.id ?? "new")} className="editRow">
        <td></td>
        <td>
          <select className="testTypeSelect" value={editField.testRequestFieldId ?? 0} title="Field type" aria-label="Field type"
            onChange={(e) => {
              const id = parseInt(e.target.value);
              const ft = (props.fieldTypes ?? []).find((f: any) => f.id === id);
              updateEditField({ ...editField, testRequestFieldId: id, testRequestFieldName: ft ? ft.name : "", label: ft ? ft.name : editField.label });
            }}>
            {availableTTFieldTypes.map((ft: any) => (
              <option key={"ttdft" + ft.id} value={ft.id}>{ft.name}</option>
            ))}
          </select>
        </td>
        <td>
          <input type="text" value={editField.label ?? ""} className="inlineEdit" style={{ width: "180px" }}
            placeholder="Label" onChange={(e) => updateEditField({ ...editField, label: e.target.value })} />
        </td>
        <td className="center" onClick={() => updateEditField({ ...editField, required: !editField.required })}>
          {editField.required ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td className="center" onClick={() => updateEditField({ ...editField, proctorProvided: !editField.proctorProvided })}>
          {editField.proctorProvided ? <MdCheckBox className="checkbox checked actionIcon readOnly" /> : <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />}
        </td>
        <td>
          <RiSave3Fill className="save actionIcon" onClick={saveDraftField} title="Save" />
          <ImCancelCircle className="cancel actionIcon" onClick={() => updateEditField({})} title="Cancel" />
        </td>
      </tr>
    );
  }

  function renderDraftField(field, index) {
    return editField && editField.id === field.id
      ? renderDraftEditRow(field)
      : renderDraftStaticRow(field, index);
  }
  async function toggleProctorProvidedAsync(e) {
    let fieldId = e.currentTarget.getAttribute("data-id");
    if (fieldId) {
      const callData = {
        companyTestTypeId: props.activeTestType.companyTestType.id,
        fieldId: parseInt(fieldId),
      };
      props.tabData.updateSpinner(true);
      var resp = await RightTimeApi.putAsync("/ToggleCompanyTestTypeFieldProctorProvided",callData);
      props.tabData.updateSpinner(false);
        if (resp && resp.ok) {
          if (resp.jsonData.proctorProvided)
            toast.success(
              "Field " + resp.jsonData.fieldLabel + " is now proctor provided"
            );
          else
            toast.success(
              "Field " + resp.jsonData.fieldLabel + " now testee provided"
            );
          props.updateData();
        }
    }
  }

  async function toggleAllProctorProvidedAsync() {
    if (!props?.activeTestType?.companyTestType?.id) return;
    if (!Array.isArray(props.activeTestType.fields) || props.activeTestType.fields.length === 0) return;

    const totalFields = props.activeTestType.fields.length;
    const providedCount = props.activeTestType.fields.filter((field) => field.proctorProvided).length;
    const shouldEnableAll = providedCount <= totalFields / 2;

    const fieldsToToggle = props.activeTestType.fields.filter((field) =>
      shouldEnableAll ? !field.proctorProvided : field.proctorProvided
    );

    if (fieldsToToggle.length === 0) return;

    props?.tabData?.updateSpinner(true);
    await Promise.all(
      fieldsToToggle.map((field) =>
        RightTimeApi.putAsync("/ToggleCompanyTestTypeFieldProctorProvided", {
          companyTestTypeId: props.activeTestType.companyTestType.id,
          fieldId: field.id,
        })
      )
    );
    props?.tabData?.updateSpinner(false);
    props.updateData();
  }

  function renderStaticRow(field, index) {
    if (fieldTypes && fieldTypes.length > 0) {
      let fieldType = fieldTypes.find(ft => ft.key === field.fieldId);
      let fieldTypeName = fieldType ? fieldType.value : (field.fieldElementName || field.id);
      const isDragging = draggedFieldId === field.id && draggedFieldContext === "company";
      const isDraggedOver = dragOverFieldId === field.id && draggedFieldContext === "company" && !isDragOverEndZone;
      return (
        <tr
          key={"tt" + field.id}
          onDragOver={(e) => handleFieldDragOver(e, field.id, "company")}
          onDragLeave={handleFieldDragLeave}
          onDrop={(e) => handleFieldDropAtTarget(e, field.id, "company")}
          className={`draggableFieldRow${isDragging ? " isDragging" : ""}${isDraggedOver ? " isDragOver" : ""}`}
        >
          <th
            className={`dragHandleCell${isDragging ? " isGrabbing" : ""}`}
            onClick={(e) => e.stopPropagation()}
            draggable={!isEditingAnyField}
            onDragStart={(e) => handleFieldDragStart(e, field.id, "company")}
            onDragEnd={handleFieldDragEnd}
            data-help-id="dragHandle"
            title="Drag to reorder"
          >
            <RxDragHandleDots1 className="actionIcon dragHandle" />
          </th>
          <th>{fieldTypeName}</th>
          <td>{field.label}</td>
          <td className="center" onClick={toggleFormFieldRequiredAsync} data-id={field.id} data-help-id="requiredField">
            {field.required ? (
              <MdCheckBox className="checkbox checked actionIcon readOnly" />
            ) : (
              <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
            )}
          </td>
          <td className="center" onClick={toggleProctorProvidedAsync} data-id={field.id} data-help-id="proctorProvided">
            {!field.proctorProvided ? (
              <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
            ) : (
              <MdCheckBox className="checkbox checked actionIcon readOnly" />
            )}
          </td>
          <td>{renderActionField(field, false)}</td>
        </tr>
      );
    }
  }

  function renderEditRow(field, index) {
    return (
      <tr key={"tt" + field.id} className="editRow">
        <td></td>
        <td>
          {/* {(() => { debugger })()} */}
          <select data-field="fieldId" title="Field type" aria-label="Field type"
            data-type="int" 
            className="testTypeSelect"
            onChange={updateFormDataAsync} 
            value={editField.fieldId}>
            {fieldTypes
              ? fieldTypes.map((o) => {
                return (
                  <option key={"field" + o.key} value={o.key}>
                    {o.value}
                  </option>
                );
              })
              : null}
          </select>
        </td>
        <td>
          {/* {(()=>{debugger})()} */}
          <input
            type="text"
            value={editField.label}
            className="inlineEdit"
            style={{ width: "180px" }}
            placeholder="Label"
            data-field="label"
            onChange={updateFormDataAsync}
          />
        </td>
        <td className="center actions requiredCell" 
          onClick={updateFormDataAsync} 
          data-type="boolean" 
          data-field="required">
          {editField.required ? (
            <MdCheckBox className="checkbox checked actionIcon readOnly" />
          ) : (
            <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
          )}
        </td>
        <td className="center proctorProvidedCell" 
          onClick={updateFormDataAsync} 
          data-type="boolean" 
          data-field="proctorProvided">
          {editField.proctorProvided ? (
            <MdCheckBox className="checkbox checked actionIcon readOnly" />
          ) : (
            <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
          )}
        </td>
        <td>{renderActionField(field, true)}</td>
      </tr>
    );
  }

  function renderfield(field, index) {
    return editField && editField.id === field.id ?
      renderEditRow(field, index) :
      renderStaticRow(field, index);
  }

  function renderTestTypeRow(field) {
    return (
      <tr key={"ttf" + field.id}>
        <td></td>
        <td>{field.testRequestFieldName}</td>
        <td>
          <input
            type="text"
            value={field.label}
            className="inlineEdit"
            style={{ width: "180px" }}
            placeholder="Label"
            onChange={(e) => props.onFieldLabelChange && props.onFieldLabelChange(field.id, e.target.value)}
          />
        </td>
        <td className="center">
          {field.required ? (
            <MdCheckBox className="checkbox checked actionIcon readOnly" />
          ) : (
            <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
          )}
        </td>
        <td className="center">
          {field.proctorProvided ? (
            <MdCheckBox className="checkbox checked actionIcon readOnly" />
          ) : (
            <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
          )}
        </td>
        <td></td>
      </tr>
    );
  }

  return (
    <div className="tableWrapper factTable nohover requestFormFieldsTable scrollable">
      <table className="nohover formFields">
        <caption>
          <span className="title">Request Form Fields</span>
          {(!isTestTypeMode || isTestTypeAdminMode || isTestTypeDraftMode) && (
            <span className="captionRight">
              <FaPlusSquare
                className="actionIcon plusIcon addField"
                onClick={isTestTypeAdminMode ? addTTField : (isTestTypeDraftMode ? addDraftField : addField)}
                data-help-id={"addTestTypeField"}
                title={isTestTypeAdminMode ? "Add field to this test type" : "Create Status specific to this test type"}
              />
            </span>
          )}
        </caption>
        <thead>
          <tr>
            <th></th>
            <th>Field</th>
            <th>Label</th>
            <th
              className="toggleColumnHeader"
              data-help-id="toggleActive"
              onClick={isTestTypeDraftMode ? toggleAllRequiredDraft : toggleAllRequiredAsync}
            >
              <span className="toggleHeaderLabel">Required</span>
              <span className="toggleHeaderIcon" aria-hidden="true">
                <MdCameraswitch />
              </span>
            </th>
            <th
              className="toggleColumnHeader"
              data-help-id="toggleActive"
              onClick={isTestTypeDraftMode ? toggleAllProctorProvidedDraft : toggleAllProctorProvidedAsync}
            >
                <span className="toggleHeaderLabel">Proctor<br />Provided</span>
                <span className="toggleHeaderIcon" aria-hidden="true">
                  <MdCameraswitch />
                </span>
            </th>
            <th className="centered"></th>
          </tr>
        </thead>
        <tbody>
          {isTestTypeMode
            ? (
              <>
                {isTestTypeDraftMode && draftFields && draftFields.map((f, i) => renderDraftField(f, i))}
                {!isTestTypeDraftMode && props.testTypeFields && props.testTypeFields.map((f, i) =>
                  isTestTypeAdminMode ? renderTTField(f, i) : renderTestTypeRow(f)
                )}
                {isTestTypeDraftMode && renderDropZoneRow("testTypeDraft")}
                {isTestTypeAdminMode && renderDropZoneRow("testTypeAdmin")}
                {isTestTypeAdminMode && editField && editField.id === 0 && renderTTAdminEditRow(editField)}
                {isTestTypeDraftMode && editField && editField.id === 0 && renderDraftEditRow(editField)}
              </>
            )
            : (
              <>
                {editField && editField.id === 0
                  ? renderfield(editField, 0)
                  : null}
                {props.activeTestType && props.activeTestType.fields
                  ? props.activeTestType.fields.map((f, i) => renderfield(f, i))
                  : null}
                {renderDropZoneRow("company")}
              </>
            )}
        </tbody>
      </table>
    </div>
  );
}

